/**
 * Datadog Configuration Module
 *
 * Reads environment variables and provides typed configuration for Datadog RUM and Logs SDKs.
 * Configuration is optional - when missing, Datadog SDK calls are skipped gracefully.
 */

export type DatadogEnvironment = 'development' | 'uat' | 'production';

export type DatadogFrontendConfig = {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: DatadogEnvironment;
};

/**
 * Environment variable names for frontend Datadog configuration.
 * These must be prefixed with VITE_ to be exposed to the frontend build.
 */
const ENV_VARS = {
  APPLICATION_ID: 'VITE_DATADOG_APPLICATION_ID',
  CLIENT_TOKEN: 'VITE_DATADOG_CLIENT_TOKEN',
  SITE: 'VITE_DATADOG_SITE',
  SERVICE: 'VITE_DATADOG_SERVICE',
  ENV: 'VITE_DATADOG_ENV',
} as const;

/**
 * Checks if the current environment is development or UAT.
 * Used to decide whether to show warnings for missing configuration.
 */
export const isDevOrUat = (): boolean => {
  const env = import.meta.env[ENV_VARS.ENV];
  return !env || env === 'development' || env === 'uat';
};

/**
 * Reads and validates Datadog configuration from environment variables.
 * Returns null if required variables are missing (graceful degradation).
 */
export const getDatadogConfig = (): DatadogFrontendConfig | null => {
  const applicationId = import.meta.env[ENV_VARS.APPLICATION_ID];
  const clientToken = import.meta.env[ENV_VARS.CLIENT_TOKEN];
  const site = import.meta.env[ENV_VARS.SITE];
  const service = import.meta.env[ENV_VARS.SERVICE];
  const env = import.meta.env[ENV_VARS.ENV] as DatadogEnvironment | undefined;

  // All required fields must be present
  if (!applicationId || !clientToken || !site || !service || !env) {
    return null;
  }

  return {
    applicationId,
    clientToken,
    site,
    service,
    env,
  };
};
